// Colourways: the colours a design is worn in. Each is four colours whose
// jobs depend on the family:
//   Editorial  paper (ground), ink (type and dark ground), accent (soft tone
//              on ink: labels, numbers), pop (a lighter soft tone)
//   Poster     paper (the loud bright), ink (near black), accent (the strong
//              ground under bright type), pop (as paper)
//   Quiet      paper (ground), ink (type), accent (a second soft ground),
//              pop (a small highlight)
//   Layouts    paper (ground), ink (words on it), accent (the strong colour:
//              blocks, discs, and the ground of the bold layouts), pop (a
//              soft block) and a fifth, on (words on the accent). Each theme
//              also sets the headline face (type).
// "Your brand" and "Match my photo" are worked out from one colour, with
// contrast checked, so any brand colour reads.
import { normHex, mix, contrast, luminance } from '../../email-composer/email/theme'

export const COLOURWAYS = {
  editorial: [
    { id: 'burgundy', name: 'Burgundy', colours: ['#F4EDE2', '#561E2B', '#D8B8C6', '#EDC7CE'] },
    { id: 'forest', name: 'Forest', colours: ['#EEF0E6', '#1F3A2E', '#B9CBB0', '#D9E4D1'] },
    { id: 'navy', name: 'Navy', colours: ['#EEF1F5', '#1C2A44', '#AFC0DA', '#D3DDEB'] },
    { id: 'blush', name: 'Ink and blush', colours: ['#FFFFFF', '#1A1A1E', '#F2C4CE', '#F8DDE3'] },
    { id: 'terracotta', name: 'Terracotta', colours: ['#F7EDE4', '#7A3522', '#E7B49A', '#F1D2C2'] },
    { id: 'espresso', name: 'Espresso', colours: ['#F2ECE4', '#3B2A20', '#D9C3A5', '#E9DCC8'] },
  ],
  poster: [
    { id: 'cobalt', name: 'Cobalt and acid', colours: ['#DEFA48', '#171B19', '#2445D8', '#DEFA48'] },
    { id: 'red', name: 'Red and cream', colours: ['#F6EFE3', '#1B1B1B', '#C8302A', '#F6EFE3'] },
    { id: 'acid', name: 'Black and acid', colours: ['#DEFA48', '#0E0E0E', '#0E0E0E', '#DEFA48'] },
    { id: 'pink', name: 'Pink and green', colours: ['#FFC7DB', '#0F3B2E', '#0C5C44', '#FFC7DB'] },
    { id: 'mint', name: 'Plum and mint', colours: ['#C9F2DD', '#2A1030', '#5B1F6A', '#C9F2DD'] },
    { id: 'orange', name: 'Orange and navy', colours: ['#FFB36B', '#141414', '#1E2A5A', '#FFB36B'] },
  ],
  quiet: [
    { id: 'peach', name: 'Peach and lilac', colours: ['#F4DFD3', '#733C33', '#D5C7EB', '#CB715B'] },
    { id: 'sage', name: 'Sage', colours: ['#E6ECE2', '#3E4E3A', '#C9D8C3', '#7F9A75'] },
    { id: 'sky', name: 'Sky', colours: ['#E3EDF5', '#2D4459', '#C4D8EA', '#5F86AB'] },
    { id: 'butter', name: 'Butter', colours: ['#F7EFD8', '#5A4520', '#EEDFAE', '#B8903A'] },
    { id: 'rose', name: 'Rose', colours: ['#F7E1E4', '#6B2737', '#EFC3CB', '#C24D66'] },
    { id: 'stone', name: 'Stone', colours: ['#EFEBE6', '#3A3633', '#DAD3CB', '#8C7F74'] },
  ],
  layouts: [
    { id: 'paper', name: 'Paper', type: 'serif', colours: ['#F3EDE2', '#262821', '#A94D32', '#DFD6C4', '#FFF2DF'] },
    { id: 'electric', name: 'Electric', type: 'heavy', colours: ['#E3F65B', '#17221B', '#2447D9', '#D1E3A4', '#E3F65B'] },
    { id: 'rose', name: 'Rose', type: 'serif', colours: ['#F2CCD7', '#641E37', '#641E37', '#F9E6EC', '#F2CCD7'] },
    { id: 'sage', name: 'Sage', type: 'sans', colours: ['#E4E8D2', '#30452E', '#506649', '#CDD5B7', '#F5F1E4'] },
    { id: 'cherry', name: 'Cherry', type: 'heavy', colours: ['#F4AAD0', '#701B32', '#701B32', '#F8D4E5', '#F4AAD0'] },
    { id: 'olive', name: 'Olive', type: 'serif', colours: ['#F3EDE0', '#293B2D', '#344A35', '#D9DDBA', '#F3EDE0'] },
    { id: 'lilac', name: 'Lilac', type: 'serif', colours: ['#D9CCF3', '#44274D', '#44274D', '#EFE6FC', '#D9CCF3'] },
  ],
}

// Darken towards near-black until `hex` reaches `min` contrast on `against`
export const deepen = (hex, against, min) => {
  for (let t = 0; t <= 1.001; t += 0.05) {
    const c = mix(hex, '#111111', t)
    if (contrast(c, against) >= min) return c
  }
  return '#111111'
}
// Lighten towards white until `hex` reaches `min` contrast on `against`
export const lighten = (hex, against, min) => {
  for (let t = 0; t <= 1.001; t += 0.05) {
    const c = mix(hex, '#FFFFFF', t)
    if (contrast(c, against) >= min) return c
  }
  return '#FFFFFF'
}

// The four colours for a family, worked out from one colour
export const fromBase = (family, base) => {
  const b = normHex(base) || '#29282D'
  if (family === 'layouts') {
    const paper = mix(b, '#FFFFFF', 0.82)
    const ink = deepen(mix(b, '#111111', 0.6), paper, 9)
    const accent = deepen(b, '#FFFFFF', 4.8)
    const on = lighten(mix(b, '#FFFFFF', 0.9), accent, 4.5)
    const soft = mix(b, '#FFFFFF', 0.64)
    const pop = contrast(ink, soft) >= 5 ? soft : mix(b, '#FFFFFF', 0.8)
    return [paper, ink, accent, pop, on]
  }
  if (family === 'poster') {
    const paper = mix(b, '#FFFFFF', 0.84)
    const accent = deepen(b, paper, 4.5)
    const ink = deepen(mix(b, '#111111', 0.8), paper, 12)
    return [paper, ink, accent, paper]
  }
  if (family === 'quiet') {
    const paper = mix(b, '#FFFFFF', 0.86)
    const ink = deepen(mix(b, '#222222', 0.45), paper, 7)
    const accent = mix(b, '#FFFFFF', 0.7)
    const soft = contrast(ink, accent) >= 5 ? accent : mix(b, '#FFFFFF', 0.8)
    const pop = deepen(b, paper, 3)
    return [paper, ink, soft, pop]
  }
  // editorial
  const ink = deepen(b, '#FFFFFF', 10)
  const paper = mix(b, '#FFFFFF', 0.93)
  const accent = lighten(mix(b, '#FFFFFF', 0.6), ink, 4.5)
  const pop = mix(b, '#FFFFFF', 0.8)
  return [paper, ink, accent, pop]
}

// "Match my photo": the colour of the photo's surroundings (its edges), or
// its strongest colour when the backdrop is plain grey or white
export const colourFromImage = (img) => {
  if (!img || !img.naturalWidth || typeof document === 'undefined') return null
  try {
    const n = 40
    const c = document.createElement('canvas')
    c.width = n
    c.height = n
    const ctx = c.getContext('2d')
    ctx.drawImage(img, 0, 0, n, n)
    const px = ctx.getImageData(0, 0, n, n).data
    const edge = [0, 0, 0, 0]
    const all = []
    for (let y = 0; y < n; y += 1) {
      for (let x = 0; x < n; x += 1) {
        const i = (y * n + x) * 4
        const rgb = [px[i], px[i + 1], px[i + 2]]
        all.push(rgb)
        if (x < 6 || y < 6 || x >= n - 6 || y >= n - 6) { edge[0] += rgb[0]; edge[1] += rgb[1]; edge[2] += rgb[2]; edge[3] += 1 }
      }
    }
    const avg = edge.slice(0, 3).map(v => v / edge[3])
    const sat = (rgb) => { const mx = Math.max(...rgb) / 255; const mn = Math.min(...rgb) / 255; return mx === 0 ? 0 : (mx - mn) / mx }
    let pick = avg
    if (sat(avg) < 0.14) {
      // A plain backdrop: use the most colourful pixels instead
      const colourful = all.filter(rgb => sat(rgb) > 0.25 && Math.max(...rgb) > 60).sort((a, b) => sat(b) - sat(a)).slice(0, 120)
      if (colourful.length > 20) pick = colourful.reduce((a, rgb) => [a[0] + rgb[0], a[1] + rgb[1], a[2] + rgb[2]], [0, 0, 0]).map(v => v / colourful.length)
    }
    return `#${pick.map(v => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase()}`
  } catch (e) {
    // A photo without CORS headers can't be read
    return null
  }
}

// Which colourways a post can wear: its brand (when the salon has a brand
// colour), its photo (when the design has one), then the curated set
export const colourwaysFor = (family, { brandColour, photoColour } = {}) => {
  const list = []
  if (normHex(brandColour)) list.push({ id: 'brand', name: 'Your brand', colours: fromBase(family, brandColour) })
  if (normHex(photoColour)) list.push({ id: 'photo', name: 'Match my photo', colours: fromBase(family, photoColour) })
  return [...list, ...(COLOURWAYS[family] || COLOURWAYS.editorial)]
}

// The colours and the in-betweens every design uses (on, words on the
// accent, is the layouts' fifth; elsewhere it's the paper)
export const deriveColours = (id, [paper, ink, accent, pop, on]) => ({
  id,
  paper, ink, accent, pop,
  on: on || paper,
  // Secondary text on each ground
  mutedOnPaper: mix(ink, paper, 0.3),
  mutedOnInk: mix(paper, ink, 0.28),
  // Photo placeholders
  holder: mix(paper, ink, 0.1),
  holderInk: mix(ink, paper, 0.35),
  dark: luminance(paper) < 0.3,
})

// The colours a post draws with
export const coloursFor = (family, colourway, { brandColour, photoColour } = {}) => {
  const list = colourwaysFor(family, { brandColour, photoColour })
  const found = list.find(c => c.id === colourway) || list[0]
  return deriveColours(found.id, found.colours)
}

// What each colour is called in the Colours tool, per family. In a poster
// the highlight is always the bright, so it follows it.
export const COLOUR_ROLES = {
  editorial: [{ key: 'paper', name: 'Background' }, { key: 'ink', name: 'Words' }, { key: 'accent', name: 'Accent' }, { key: 'pop', name: 'Soft accent' }],
  poster: [{ key: 'accent', name: 'Main colour' }, { key: 'paper', name: 'Bright' }, { key: 'ink', name: 'Dark' }],
  quiet: [{ key: 'paper', name: 'Background' }, { key: 'ink', name: 'Words' }, { key: 'accent', name: 'Soft colour' }, { key: 'pop', name: 'Highlight' }],
  layouts: [{ key: 'paper', name: 'Background' }, { key: 'ink', name: 'Words' }, { key: 'accent', name: 'Main colour' }, { key: 'on', name: 'Words on the main colour' }, { key: 'pop', name: 'Soft colour' }],
}

// A colourway with the salon's own changes on top (post.tune.colours)
export const tunedColours = (base, over, family) => {
  const keys = ['paper', 'ink', 'accent', 'pop', 'on']
  const own = {}
  keys.forEach((k) => { const v = normHex(over && over[k]); if (v) own[k] = v })
  if (!Object.keys(own).length) return base
  if (family === 'poster' && own.paper && !own.pop) own.pop = own.paper
  return { ...deriveColours(base.id, keys.map(k => own[k] || base[k])), custom: true }
}

// Words on a ground that's hard to read: the pairs worth checking per family
export const READ_PAIRS = {
  editorial: [['ink', 'paper'], ['accent', 'ink']],
  poster: [['paper', 'accent'], ['ink', 'paper']],
  quiet: [['ink', 'paper'], ['ink', 'accent']],
  layouts: [['ink', 'paper'], ['on', 'accent'], ['ink', 'pop']],
}
export const hardToRead = (family, c) => (READ_PAIRS[family] || READ_PAIRS.editorial)
  .filter(([fg, bg]) => contrast(c[fg], c[bg]) < 3)
  .map(([fg, bg]) => ({ fg, bg, ratio: contrast(c[fg], c[bg]) }))
// Four colours for a family built around a colour someone asked for ("make
// it pink"): the colour itself stays visible (as a poster's main colour, or
// the words and soft grounds elsewhere), then every pair is made readable
export const fromColour = (family, hex) => {
  const b = normHex(hex) || '#29282D'
  let c
  if (family === 'poster') {
    c = { accent: b, paper: '#FFFFFF', ink: '#141414', pop: '#FFFFFF' }
    return fixColoursFull('poster', c, 'accent')
  }
  if (family === 'quiet') {
    const paper = mix(b, '#FFFFFF', 0.8)
    c = { paper, ink: deepen(b, paper, 6), accent: mix(b, '#FFFFFF', 0.6), pop: deepen(b, paper, 3) }
    return fixColoursFull('quiet', c, 'paper')
  }
  if (family === 'layouts') {
    // The colour asked for is the main colour, with words that read on it
    const paper = mix(b, '#FFFFFF', 0.85)
    c = { paper, ink: deepen(b, paper, 7), accent: b, pop: mix(b, '#FFFFFF', 0.65), on: contrast('#FFFFFF', b) >= 4.5 ? '#FFFFFF' : '#141414' }
    return fixColoursFull('layouts', c, 'accent')
  }
  const paper = mix(b, '#FFFFFF', 0.9)
  c = { paper, ink: deepen(b, paper, 5), accent: mix(b, '#FFFFFF', 0.55), pop: mix(b, '#FFFFFF', 0.78) }
  return fixColoursFull('editorial', c, 'ink')
}
const fixColoursFull = (family, c, keep) => {
  const next = { ...c, ...fixColours(family, c, keep) }
  return family === 'layouts' ? [next.paper, next.ink, next.accent, next.pop, next.on] : [next.paper, next.ink, next.accent, next.pop]
}

// Make every pair readable again, keeping the colour the salon just chose
// (`keep`) and moving the others as little as possible. The colours depend on
// each other (a poster's bright is both words on the main colour and the
// ground under the dark), so they're solved together, not one at a time.
const distance = (a, b) => {
  const x = (normHex(a) || '#000000').slice(1)
  const y = (normHex(b) || '#000000').slice(1)
  return [0, 2, 4].reduce((sum, i) => sum + Math.abs(parseInt(x.slice(i, i + 2), 16) - parseInt(y.slice(i, i + 2), 16)), 0)
}
export const fixColours = (family, c, keep) => {
  const pairs = READ_PAIRS[family] || READ_PAIRS.editorial
  const roles = Array.from(new Set(pairs.flat())).filter(k => k !== keep)
  const options = (k) => {
    const v = c[k]
    const list = [v, mix(v, '#FFFFFF', 0.85), mix(v, '#111111', 0.8), '#FFFFFF', '#141414']
    pairs.forEach(([fg, bg]) => {
      const other = fg === k ? bg : bg === k ? fg : null
      if (other) list.push(deepen(v, c[other], 4.5), lighten(v, c[other], 4.5))
    })
    return Array.from(new Set(list.map(h => h.toUpperCase())))
  }
  let best = null
  const walk = (i, pick) => {
    if (i === roles.length) {
      const next = { ...c, ...pick }
      const bad = pairs.reduce((a, [fg, bg]) => a + Math.max(0, 4.5 - contrast(next[fg], next[bg])), 0)
      const moved = roles.reduce((a, k) => a + distance(c[k], next[k]), 0)
      const score = bad * 10000 + moved
      if (!best || score < best.score) best = { score, pick }
      return
    }
    options(roles[i]).forEach(v => walk(i + 1, { ...pick, [roles[i]]: v }))
  }
  walk(0, {})
  const out = {}
  roles.forEach((k) => { if (best && best.pick[k] && best.pick[k].toUpperCase() !== String(c[k]).toUpperCase()) out[k] = best.pick[k] })
  if (family === 'poster' && out.paper) out.pop = out.paper
  return out
}

// A handful of a photo's own colours (for picking colours from it)
export const paletteFromImage = (img, count = 6) => {
  if (!img || !img.naturalWidth || typeof document === 'undefined') return []
  try {
    const n = 48
    const c = document.createElement('canvas')
    c.width = n
    c.height = n
    const ctx = c.getContext('2d')
    ctx.drawImage(img, 0, 0, n, n)
    const px = ctx.getImageData(0, 0, n, n).data
    // Bucket colours coarsely, then take the busiest buckets that aren't too alike
    const buckets = new Map()
    for (let i = 0; i < px.length; i += 4) {
      const key = `${px[i] >> 4},${px[i + 1] >> 4},${px[i + 2] >> 4}`
      const b = buckets.get(key) || { n: 0, r: 0, g: 0, b: 0 }
      b.n += 1; b.r += px[i]; b.g += px[i + 1]; b.b += px[i + 2]
      buckets.set(key, b)
    }
    const hex = (v) => Math.round(v).toString(16).padStart(2, '0')
    const ranked = Array.from(buckets.values()).sort((a, b) => b.n - a.n)
      .map(b => `#${hex(b.r / b.n)}${hex(b.g / b.n)}${hex(b.b / b.n)}`.toUpperCase())
    const out = []
    ranked.forEach((h) => { if (out.length < count && out.every(o => contrast(o, h) > 1.35)) out.push(h) })
    return out
  } catch (e) {
    return []
  }
}

// The nearest colourway between the three styles and the layouts' themes
const NEAREST = {
  'layouts>editorial': { paper: 'terracotta', electric: 'navy', rose: 'burgundy', sage: 'forest', cherry: 'burgundy', olive: 'forest', lilac: 'blush' },
  'layouts>poster': { paper: 'red', electric: 'cobalt', rose: 'pink', sage: 'mint', cherry: 'red', olive: 'pink', lilac: 'mint' },
  'layouts>quiet': { paper: 'stone', electric: 'sky', rose: 'rose', sage: 'sage', cherry: 'rose', olive: 'sage', lilac: 'peach' },
  'editorial>layouts': { burgundy: 'rose', forest: 'sage', navy: 'electric', blush: 'rose', terracotta: 'paper', espresso: 'paper' },
  'poster>layouts': { cobalt: 'electric', red: 'paper', acid: 'electric', pink: 'rose', mint: 'sage', orange: 'paper' },
  'quiet>layouts': { peach: 'paper', sage: 'sage', sky: 'electric', butter: 'paper', rose: 'rose', stone: 'paper' },
}

// Moving to another family keeps "brand" and "photo", and otherwise takes the
// colourway in the same place in that family's list
export const colourwayInFamily = (colourway, from, to) => {
  if (colourway === 'brand' || colourway === 'photo') return colourway
  const near = NEAREST[`${from}>${to}`]
  if (near && near[colourway]) return near[colourway]
  const i = (COLOURWAYS[from] || []).findIndex(c => c.id === colourway)
  const list = COLOURWAYS[to] || COLOURWAYS.editorial
  return (list[Math.max(0, i)] || list[0]).id
}
